import type { Conversation, DeskAgent, DeskDetail } from '@deskworks/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Archive, Bot, Crown, Maximize2, MessageSquarePlus, MessagesSquare, MoreHorizontal, Pencil } from 'lucide-react';
import { useRef, useState } from 'react';
import { AgentAvatar } from '@/components/agent-avatar';
import { ChatScreen } from '@/components/chat/chat-screen';
import { ConversationThread, effectiveAgentState, failedMessage, useConversationLive } from '@/components/chat/conversation-thread';
import { talkMentions, talkPeer, useDeskMentions } from '@/components/chat/desk-talk';
import { ModelOnboarding } from '@/components/chat/model-onboarding';
import { LeadAgentPicker } from '@/components/lead-agent-picker';
import { ChatWritesSwitch, CONVERSATION_RULES } from '@/components/conversations';
import { RelativeTime } from '@/components/relative-time';
import { ConversationStateBadge } from '@/components/status-badge';
import { ErrorState, LoadingRows } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { api, errorMessage } from '@/lib/api';
import { useAgents, useApiMutation, useIsPhone, useRuntimeProfiles } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { agentRuntimeLabel } from '@/lib/labels';
import { qk } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

/**
 * The desk's chats, laid out like a chat app: every conversation held on the desk in a list on the left, the chosen one
 * on the right to read and carry on. Without a choice it follows the newest chat; "New chat" starts an empty one with
 * the Lead or another agent.
 */
export function DeskChat({ desk }: { desk: DeskDetail }) {
  if (!desk.leadAgent) return <NoLeadAgent desk={desk} />;
  return <DeskChats desk={desk} lead={desk.leadAgent} />;
}

function DeskChats({ desk, lead }: { desk: DeskDetail; lead: DeskAgent }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const phone = useIsPhone();
  const { chat } = useSearch({ from: '/_authed/w/$workspaceId/desks/$deskId' });
  const conversations = useQuery({
    queryKey: qk.deskConversations(desk.id),
    queryFn: () => api.conversations.list(desk.id),
  });
  // The agent of a new, still empty chat; null = show the chosen (or newest) conversation.
  const [draft, setDraft] = useState<string | null>(null);
  // On a phone the list comes first; a chat opens over it (docs/features/mobile.md#chats).
  const conversationId = draft ? null : (chat ?? (phone ? null : conversations.data?.[0]?.id) ?? null);
  const { query: conv, live } = useConversationLive(conversationId);
  // A chat opened from the list on a phone is a step in the browser's history: back (and the back gesture) closes it.
  const pushed = useRef(false);

  const select = (id: string | undefined, { open = false } = {}) => {
    setDraft(null);
    pushed.current = open && phone;
    void navigate({ to: '.', search: (s) => ({ ...s, chat: id }), replace: !pushed.current });
  };
  const close = () => {
    setDraft(null);
    if (pushed.current && chat) {
      pushed.current = false;
      window.history.back();
    } else if (chat) {
      void navigate({ to: '.', search: (s) => ({ ...s, chat: undefined }), replace: true });
    }
  };

  const listed = conversations.data?.find((c) => c.id === conversationId);
  const detail = conversationId ? (conv.data ?? null) : null;
  const agentId = draft ?? detail?.agentId ?? listed?.agentId ?? lead.agentId;
  const deskAgent = desk.agents.find((a) => a.agentId === agentId);
  const agentName = deskAgent?.name ?? detail?.agentName ?? listed?.agentName ?? lead.name;
  const isLead = agentId === lead.agentId;

  const agents = useAgents(desk.workspaceId);
  const profiles = useRuntimeProfiles(desk.workspaceId);
  const agent = agents.data?.find((a) => a.id === agentId);
  const profile = profiles.data?.find((p) => p.id === agent?.runtimeProfileId) ?? agent?.runtimeProfile ?? null;
  const runtime = !agent ? null : !profile ? t('No runtime') : profile.runtimeType === 'local' ? t('Demo without AI') : agentRuntimeLabel(agent, profile);

  const create = async (content: string) => {
    const created = await api.conversations.create(desk.id, { agentId, message: content });
    queryClient.setQueryData(qk.conversation(created.id), created);
    select(created.id, { open: !chat && phone });
    void queryClient.invalidateQueries({ queryKey: qk.deskConversations(desk.id) });
  };

  // A chat with another desk or VICKI (docs/features/desk-talk.md): people ask in it with @; elsewhere @ asks other desks.
  const talk = (detail ?? listed)?.kind === 'desk_talk' ? (detail ?? listed)! : null;
  const deskMentions = useDeskMentions(desk.workspaceId, desk.id);
  const mentions = talk ? talkMentions(talk, desk.id, desk.agents.filter((a) => a.enabled || a.role === 'lead')) : deskMentions;
  const state = effectiveAgentState(detail, live.awaitingReply);
  const loading = conversations.isPending || (!!conversationId && conv.isPending);
  const chatAgents = desk.agents.filter((a) => a.enabled || a.role === 'lead');

  const newChat =
    chatAgents.length > 1 ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size={phone ? 'sm' : 'xs'} className="bg-card" title={t('Start a new chat')}>
            <MessageSquarePlus /> {t('New chat')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>{t('Talk to')}</DropdownMenuLabel>
          {chatAgents.map((a) => (
            <DropdownMenuItem key={a.agentId} onSelect={() => setDraft(a.agentId)}>
              {a.role === 'lead' ? <Crown /> : <Bot />} {a.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    ) : (
      <Button variant="outline" size={phone ? 'sm' : 'xs'} className="bg-card" onClick={() => setDraft(lead.agentId)} title={t('Start a new chat')}>
        <MessageSquarePlus /> {t('New chat')}
      </Button>
    );

  const list = conversations.isPending ? (
    <div className="px-2">
      <LoadingRows rows={3} />
    </div>
  ) : conversations.isError ? null : conversations.data.length === 0 && !draft ? (
    <p className="px-2 py-6 text-center text-xs text-muted-foreground">
      {phone ? t('No chats yet. Start one with “New chat”.') : t('No chats yet. Write the first message on the right.')}
    </p>
  ) : (
    <ul className="flex flex-col gap-0.5">
      {draft && !phone && (
        <li className="rounded-md bg-accent px-2.5 py-2">
          <div className="truncate text-sm font-medium">{t('New chat')}</div>
          <div className="truncate text-xs text-muted-foreground">{agentName}</div>
        </li>
      )}
      {conversations.data.map((c) => (
        <ChatRow
          key={c.id}
          conv={c}
          active={!phone && c.id === conversationId}
          roomy={phone}
          onSelect={() => select(c.id, { open: true })}
          onArchived={() => c.id === conversationId && select(undefined)}
        />
      ))}
    </ul>
  );

  const thread = loading ? (
    <div className="p-4">
      <LoadingRows rows={3} />
    </div>
  ) : conversations.isError ? (
    <div className="p-4">
      <ErrorState error={conversations.error} onRetry={() => void conversations.refetch()} />
    </div>
  ) : conversationId && conv.isError ? (
    <div className="p-4">
      <ErrorState error={conv.error} onRetry={() => void conv.refetch()} />
    </div>
  ) : (
    <ConversationThread
      key={conversationId ?? `new-${agentId}`}
      className="flex-1"
      conv={detail}
      agentName={agentName}
      workspaceId={desk.workspaceId}
      live={live}
      onCreate={create}
      autoFocus={phone && !!draft}
      mentions={mentions}
      placeholder={
        talk
          ? t('Ask {name} — or anyone with @ … (Enter to send)', { name: mentions?.fallback?.name ?? agentName })
          : t('Ask {name} anything about this desk … (Enter to send)', { name: agentName })
      }
      emptyState={
        <div className="flex flex-col items-center gap-2 py-8 text-center">
          <div className="flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <MessagesSquare className="size-5" />
          </div>
          <div className="text-sm font-medium">{t('Ask {name} anything about this desk …', { name: agentName })}</div>
          <p className="max-w-md text-xs text-muted-foreground">
            {t('e.g. “What’s on today?” or “Explain where CASE-123 stands”.')} {CONVERSATION_RULES}
          </p>
        </div>
      }
      banner={<ModelOnboarding agentId={agentId} workspaceId={desk.workspaceId} failedText={failedMessage(detail)?.content} />}
    />
  );

  if (phone) {
    const writes = detail?.canWrite && detail.status === 'active';
    return (
      <>
        <Card className="overflow-hidden">
          <div className="flex items-center gap-2 border-b px-4 py-3">
            <span className="flex-1 text-sm font-semibold">{t('Chats')}</span>
            {newChat}
          </div>
          <div className="p-2">{list}</div>
        </Card>
        {(conversationId || draft) && (
          <ChatScreen
            title={detail?.title ?? listed?.title ?? t('New chat')}
            subtitle={
              talk ? (
                t('Chat between desks')
              ) : (
                <>
                  {agentName}
                  {runtime && <> · {runtime}</>}
                </>
              )
            }
            actions={state && <ConversationStateBadge state={state} />}
            toolbar={writes && detail ? <ChatWritesSwitch conv={detail} /> : undefined}
            onBack={close}
          >
            {thread}
          </ChatScreen>
        )}
      </>
    );
  }

  return (
    <Card className="flex h-[min(720px,78vh)] min-h-[460px] flex-col overflow-hidden md:flex-row">
      <aside className="flex max-h-64 shrink-0 flex-col border-b bg-muted/30 md:max-h-none md:w-72 md:border-r md:border-b-0">
        <div className="flex items-center gap-2 px-3 py-3">
          <span className="flex-1 px-1 text-sm font-semibold">{t('Chats')}</span>
          {newChat}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">{list}</div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-4 py-3">
          {talk ? (
            <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <MessagesSquare className="size-4" />
            </div>
          ) : (
            <AgentAvatar seed={agentId} size={36} lead={isLead} />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold">{talk ? talk.title : agentName}</span>
              {state && !talk && <ConversationStateBadge state={state} />}
            </div>
            <div className="truncate text-xs text-muted-foreground">
              {talk ? (
                t('Chat with {name}: questions and answers, no Work Items. People read along and ask with @.', { name: talkPeer(talk, desk.id)?.name ?? '' })
              ) : (
                <>
                  {isLead ? t('Lead Agent') : t('Desk agent')}
                  {runtime && <> · {runtime}</>}
                  {detail && <> · {detail.title}</>}
                </>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {detail && !talk && <ChatWritesSwitch conv={detail} />}
            {conversationId && (
              <Button asChild variant="ghost" size="xs">
                <Link to="/w/$workspaceId/conversations/$conversationId" params={{ workspaceId: desk.workspaceId, conversationId }}>
                  <Maximize2 /> {t('Open full screen')}
                </Link>
              </Button>
            )}
          </div>
        </div>
        {thread}
      </div>
    </Card>
  );
}

/** One chat in the list: click to open; its ⋯ menu renames (in place) or archives it. */
function ChatRow({
  conv,
  active,
  roomy,
  onSelect,
  onArchived,
}: {
  conv: Conversation;
  active: boolean;
  /** A phone: rows to tap, with the last message. */
  roomy?: boolean;
  onSelect: () => void;
  onArchived: () => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(conv.title);
  const talk = conv.kind === 'desk_talk';
  // The menu hands focus back to its trigger as it closes; for "Rename" the title field keeps it.
  const keepFocus = useRef(false);
  const invalidate = [qk.conversation(conv.id), qk.deskConversations(conv.deskId ?? '')];
  const rename = useApiMutation((next: string) => api.conversations.update(conv.id, { title: next }), { invalidate, errorTitle: t('Couldn’t rename the chat') });
  const archive = useApiMutation(() => api.conversations.update(conv.id, { status: 'archived' }), {
    invalidate,
    success: t('Chat archived'),
    onSuccess: onArchived,
  });

  const save = () => {
    setRenaming(false);
    const next = title.trim();
    if (next && next !== conv.title) rename.mutate(next);
    else setTitle(conv.title);
  };

  if (renaming) {
    return (
      <li className="px-1 py-1">
        <Input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') {
              setTitle(conv.title);
              setRenaming(false);
            }
          }}
          className="h-8 bg-card text-sm"
          aria-label={t('Chat title')}
        />
      </li>
    );
  }

  return (
    <li className="group relative">
      <button
        type="button"
        onClick={onSelect}
        aria-current={active ? 'true' : undefined}
        className={cn(
          'flex w-full cursor-pointer flex-col rounded-md px-2.5 py-2 pr-8 text-left hover:bg-muted',
          roomy && 'gap-0.5 py-3 pr-10 active:bg-muted',
          active && 'bg-accent hover:bg-accent',
        )}
      >
        <span className="flex items-center gap-1.5">
          {talk && <MessagesSquare className="size-3.5 shrink-0 text-primary" aria-label={t('Chat between desks')} />}
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{(rename.isPending && rename.variables) || conv.title || t('Conversation')}</span>
          {!talk && conv.agentState !== 'sleeping' && <ConversationStateBadge state={conv.agentState} />}
          {talk && (conv.openQuestions ?? 0) > 0 && <span className="shrink-0 text-xs text-primary">{t('{n} open', { n: conv.openQuestions ?? 0 })}</span>}
        </span>
        {roomy && conv.lastMessagePreview && <span className="line-clamp-2 text-[13px] text-muted-foreground">{conv.lastMessagePreview}</span>}
        <span className="truncate text-xs text-muted-foreground">
          {talk ? t('Questions between desks') : conv.agentName} · <RelativeTime value={conv.lastMessageAt ?? conv.createdAt} />
        </span>
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t('Chat options')}
            className={cn(
              'absolute top-1.5 right-1 size-7 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100',
              roomy && 'top-2 size-9 opacity-100',
            )}
          >
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-44"
          onCloseAutoFocus={(e) => {
            if (keepFocus.current) e.preventDefault();
            keepFocus.current = false;
          }}
        >
          <DropdownMenuItem
            onSelect={() => {
              keepFocus.current = true;
              setTitle(conv.title);
              setRenaming(true);
            }}
          >
            <Pencil /> {t('Rename')}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => archive.mutate()} disabled={archive.isPending}>
            <Archive /> {t('Archive')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}

function NoLeadAgent({ desk }: { desk: DeskDetail }) {
  const [pick, setPick] = useState('');
  const setLead = useApiMutation(() => api.desks.setAgent(desk.id, pick, { role: 'lead', enabled: true }), {
    invalidate: [qk.desk(desk.id), qk.desks(desk.workspaceId), qk.agentAll],
    success: t('Lead Agent set'),
  });

  return (
    <Card className="p-5">
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300">
          <Crown className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold">{t('This desk doesn’t have a Lead Agent yet')}</h3>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            {t('The Lead Agent is your point of contact on this desk: you can talk to it right here, and it decides what work needs to be done. Choose one:')}
          </p>
          <div className="mt-3 grid max-w-lg gap-2">
            <LeadAgentPicker workspaceId={desk.workspaceId} value={pick} onChange={setPick} size="sm" />
            {pick && (
              <div>
                <Button size="sm" loading={setLead.isPending} onClick={() => setLead.mutate()}>
                  {t('Make Lead Agent')}
                </Button>
              </div>
            )}
            {setLead.isError && <p className="text-xs text-destructive">{errorMessage(setLead.error)}</p>}
          </div>
        </div>
      </div>
    </Card>
  );
}
